import * as React from 'react';
import {MenuItem, Select, FormControl} from "@mui/material";
import {LineChart} from "@mui/x-charts/LineChart";
import {useEffect, useState} from "react";
import {SrsStageDistribution} from "../types/stats";
import {theme} from "../../../app/theme";
import DashboardPanel from "./DashboardPanel";

export interface StageProgressViewProps {
    stages: SrsStageDistribution
    ready: boolean
}

const maxStageValues : Map<number, number> = new Map<number, number>([
    [1, 4], [2, 8], [3, 23], [4, 2], [5, 4], [6, 14], [7, 30], [8, 120], [9, 180]
]);

export default function StageProgressChartView({stages, ready} : StageProgressViewProps) {

    const [selectedStage, setSelectedStage] = useState<number>(1);

    useEffect(() => {
        if (!ready)
            return;

        for (let s = 9; s >= 1; s--) {
            if (stages.has(s) && stages.get(s)?.size) {
                setSelectedStage(s);
                return;
            }
        }
    }, [ready])


    const xAxisValues = () : number[] => {
        const values = Array.from(stages.get(selectedStage)?.keys() ?? []);
        if (!values.length)
            return values;
        if (!values.includes(0))
            values.unshift(0);
        if (!values.includes(maxStageValues.get(selectedStage)!))
            values.push(maxStageValues.get(selectedStage)!);
        return values;
    };

    const yValues = () => {
        const baseValues = Array.from(stages.get(selectedStage)?.values() ?? []);
        if (!baseValues.length)
            return baseValues;
        const xValues = Array.from(stages.get(selectedStage)?.keys() ?? []);
        if (!xValues.includes(0))
            baseValues.unshift(0);
        if (!xValues.includes(maxStageValues.get(selectedStage)!))
            baseValues.push(0);
        return baseValues;
    };


    return (
        <DashboardPanel title="Stage Progress" ready={ready} skeletonHeight={230}
                        titleAction={
                            <FormControl sx={{ minWidth: 60 }} size="small">
                                <Select size="small"
                                        value={selectedStage}
                                        onChange={e => setSelectedStage(e.target.value as number)}
                                        renderValue={(value) => `Stage ${value}`}
                                        aria-label="Stage"
                                        sx={{
                                            color: theme.palette.secondary.contrastText,
                                            borderRadius: 2,
                                            '& .MuiOutlinedInput-notchedOutline': {
                                                border: 'none',
                                            },
                                            '& .MuiSvgIcon-root': {color: theme.palette.secondary.contrastText},
                                        }}
                                        MenuProps={{
                                            disableScrollLock: true,
                                            PaperProps: {sx: {borderRadius: 2}},
                                        }}
                                >
                                    <MenuItem value={1}>1</MenuItem>
                                    <MenuItem value={2}>2</MenuItem>
                                    <MenuItem value={3}>3</MenuItem>
                                    <MenuItem value={4}>4</MenuItem>
                                    <MenuItem value={5}>5</MenuItem>
                                    <MenuItem value={6}>6</MenuItem>
                                    <MenuItem value={7}>7</MenuItem>
                                    <MenuItem value={8}>8</MenuItem>
                                    <MenuItem value={9}>9</MenuItem>
                                </Select>
                            </FormControl>
                        }
        >
            <LineChart
                series={[{
                    data: yValues(),
                    valueFormatter: v => `${v} items`,
                    area: true,
                    showMark: false,
                    color: theme.palette.secondary.main,
                }]}
                xAxis={[{
                    scaleType: "linear",
                    data: xAxisValues(),
                    valueFormatter: s => `${s} ${selectedStage >= 4 ? "days" : "h"}`,
                    min: 0,
                    max: maxStageValues.get(selectedStage),
                }]}
                height={200}
            />
        </DashboardPanel>

    );
}
